import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';

import { useSidebarState } from '@/__core/hooks/useSidebarState';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import { calculateTableWidth } from '@/utils/tableWidth';

import { ReceiptLine } from '../types';

interface ReceiptLinesTableProps {
  lines: ReceiptLine[];
  onChangeLine: (lineId: string, updates: Partial<ReceiptLine>) => void;
  onDeleteLines: (lineIds: string[]) => void;
  itemOptions?: Array<{ id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>;
  uomOptions?: Array<{ id: string; name: string; code?: string }>;
  itemLoading?: boolean;
  allowAddItem?: boolean;
  onAddItem?: () => void;
  disabled?: boolean;
}

const editableCellSx = {
  '& .MuiInputBase-root': {
    height: '30px',
    minHeight: '30px',
    maxHeight: '30px',
    backgroundColor: 'transparent',
    overflow: 'visible',
    border: 'none !important',
    borderBottom: '1px solid #d1d5db',
    borderRadius: 0,
    '&:hover': {
      borderBottom: '1px solid #9ca3af',
    },
    '&.Mui-focused': {
      borderBottom: '1px solid #3b82f6 !important',
      border: 'none !important',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0',
    fontSize: '0.875rem',
    color: '#374151 !important',
  },
};

const PAPER_HORIZONTAL_PADDING = 0;

export default function ReceiptLinesTable({
  lines,
  onChangeLine,
  onDeleteLines,
  itemOptions = [],
  uomOptions = [],
  itemLoading = false,
  allowAddItem = false,
  onAddItem,
  disabled = false,
}: ReceiptLinesTableProps) {
  const [tableWidth, setTableWidth] = React.useState<number>(0);
  const [selectedLineIds, setSelectedLineIds] = React.useState<string[]>([]);
  const [selectedAction, setSelectedAction] = React.useState<string>('');
  const [manualItemOptions, setManualItemOptions] = React.useState<Array<{ id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>>([]);
  const [manualUomOptions, setManualUomOptions] = React.useState<Array<{ id: string; name: string; code?: string }>>([]);
  const sidebarOpen = useSidebarState();

  React.useEffect(() => {
    const handleResize = () => {
      const finalWidth = calculateTableWidth({ windowWidth: window.innerWidth, sidebarOpen });
      setTableWidth(finalWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const displayLines = lines;
  const effectiveItemOptions = React.useMemo(() => {
    const merged = [...itemOptions, ...manualItemOptions];
    const unique = new Map<string, { id: string; name: string; code: string; supplier_name?: string; unit_id?: string; unit_name?: string }>();
    merged.forEach((item) => {
      if (!unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values());
  }, [itemOptions, manualItemOptions]);
  const effectiveUomOptions = React.useMemo(() => {
    const merged = [...uomOptions, ...manualUomOptions];
    const unique = new Map<string, { id: string; name: string; code?: string }>();
    merged.forEach((uom) => {
      if (!unique.has(uom.id)) {
        unique.set(uom.id, uom);
      }
    });
    return Array.from(unique.values());
  }, [uomOptions, manualUomOptions]);
  const groupedItemOptions = React.useMemo(
    () => [...effectiveItemOptions].sort((left, right) => {
      const leftSupplier = left.supplier_name?.trim() || 'Chua co nha cung cap';
      const rightSupplier = right.supplier_name?.trim() || 'Chua co nha cung cap';

      return leftSupplier.localeCompare(rightSupplier, 'vi')
        || (left.code || '').localeCompare(right.code || '', 'vi')
        || (left.name || '').localeCompare(right.name || '', 'vi');
    }),
    [effectiveItemOptions],
  );
  const allSelected = displayLines.length > 0 && displayLines.every((line) => selectedLineIds.includes(line.id));
  const selectedCount = selectedLineIds.length;

  React.useEffect(() => {
    const itemLineIds = new Set(displayLines.map((line) => line.id));
    setSelectedLineIds((current) => current.filter((id) => itemLineIds.has(id)));
  }, [displayLines]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLineIds(displayLines.map((line) => line.id));
      return;
    }
    setSelectedLineIds([]);
  };

  const toggleSelectLine = (lineId: string, checked: boolean) => {
    setSelectedLineIds((current) => {
      if (checked) {
        if (current.includes(lineId)) {
          return current;
        }
        return [...current, lineId];
      }
      return current.filter((id) => id !== lineId);
    });
  };

  const handleDeleteSelected = () => {
    if (selectedLineIds.length === 0) {
      return;
    }
    onDeleteLines(selectedLineIds);
    setSelectedLineIds([]);
    setSelectedAction('');
  };

  const handleApplyAction = () => {
    if (!selectedAction || selectedLineIds.length === 0) {
      return;
    }

    if (selectedAction === 'delete') {
      handleDeleteSelected();
    }
  };

  const totalAmount = lines.reduce((sum, line) => {
    const received = Number(line.received_qty || 0);
    const unitCost = Number(line.unit_cost || 0);
    return sum + (isNaN(received) || isNaN(unitCost) ? 0 : received * unitCost);
  }, 0);

  return (
    <Paper
      sx={{
        padding: '0px',
        borderRadius: 1,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        marginBottom: '24px',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant='h6'
        sx={{
          fontWeight: 600,
          color: '#111827',
          marginBottom: '20px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px',
        }}
      >
        Danh sách vật tư nhập kho
      </Typography>

      <TableContainer
        sx={{
          maxWidth: '100%',
          border: '1px solid #e2e8f0',
          borderRadius: 1,
          overflowX: 'auto',
          overflowY: 'visible',
          width: tableWidth > 0 ? `${Math.max(tableWidth - PAPER_HORIZONTAL_PADDING, 320)}px` : '100%',
          boxSizing: 'border-box',
          paddingBottom: '12px',
          backgroundColor: '#fff',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px', '&:hover': { backgroundColor: '#a8a8a8' } },
        }}
      >
        <Table size='small' sx={{ minWidth: 'max-content', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell style={{ width: '48px', minWidth: '48px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>
                <Checkbox
                  size='small'
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                  disabled={disabled}
                  sx={{ p: 0.5 }}
                />
              </TableCell>
              <TableCell style={{ width: '40px', minWidth: '40px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>#</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151' }}>Mã VT</TableCell>
              <TableCell style={{ width: '280px', minWidth: '280px' }} sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
              <TableCell style={{ width: '100px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151' }}>ĐVT</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL nhận</TableCell>
              <TableCell style={{ width: '140px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Đơn giá</TableCell>
              <TableCell style={{ width: '150px', minWidth: '150px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Thành tiền</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151' }}>Số lô</TableCell>
              <TableCell style={{ width: '220px', minWidth: '220px' }} sx={{ fontWeight: 600, color: '#374151' }}>Ghi chú</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4, color: '#9ca3af', fontSize: '0.875rem' }}>
                  Chưa có dòng vật tư. Hãy chọn PO để tự sinh danh sách hoặc thêm bằng API backend.
                </TableCell>
              </TableRow>
            ) : (
              displayLines.map((line, index) => {
                const lineAmount = Number(line.received_qty || 0) * Number(line.unit_cost || 0);
                const selectedItem = effectiveItemOptions.find((item) => item.id === line.item_id) || null;
                const selectedUom = effectiveUomOptions.find((uom) => uom.id === line.uom_id) || null;
                return (
                  <TableRow key={line.id} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Checkbox
                        size='small'
                        checked={selectedLineIds.includes(line.id)}
                        onChange={(event) => toggleSelectLine(line.id, event.target.checked)}
                        disabled={disabled}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>{index + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontFamily: '"Roboto Mono", monospace' }}>
                        {selectedItem?.code || line.item_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={groupedItemOptions}
                        value={selectedItem || (line.item_id ? { id: line.item_id, name: line.item_name, code: line.item_code } : null)}
                        getOptionLabel={(option) => option?.name || ''}
                        renderOption={(props, option) => (
                          <Box component='li' {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.75 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>
                              {option.name}
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                              {option?.supplier_name?.trim() || 'Chua co nha cung cap'}
                            </Typography>
                          </Box>
                        )}
                        onChange={(_event, newValue) => {
                          const nextItem = newValue as { id: string; name: string; code: string; unit_id?: string; unit_name?: string } | null;
                          onChangeLine(line.id, {
                            item_id: nextItem?.id || '',
                            item_code: nextItem?.code || '',
                            item_name: nextItem?.name || '',
                            uom_id: nextItem?.unit_id || line.uom_id || '',
                            uom_name: nextItem?.unit_name || line.uom_name || '',
                          });
                        }}
                        onAddNew={async ({ name }) => {
                          const created = {
                            id: `manual-item-${Date.now()}`,
                            name: name.trim(),
                            code: '',
                          };
                          setManualItemOptions((current) => [...current, created]);
                          return created;
                        }}
                        placeholder='Chọn vật tư'
                        noOptionsText='Không tìm thấy vật tư'
                        loading={itemLoading}
                        disableClearable={Boolean(line.item_id)}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={effectiveUomOptions}
                        value={selectedUom || (line.uom_id ? { id: line.uom_id, name: line.uom_name } : null)}
                        onChange={(_event, newValue) => {
                          const nextUom = newValue as { id: string; name: string } | null;
                          onChangeLine(line.id, {
                            uom_id: nextUom?.id || '',
                            uom_name: nextUom?.name || '',
                          });
                        }}
                        onAddNew={async ({ name }) => {
                          const created = {
                            id: `manual-uom-${Date.now()}`,
                            name: name.trim(),
                            code: name.trim(),
                          };
                          setManualUomOptions((current) => [...current, created]);
                          return created;
                        }}
                        placeholder='Chọn ĐVT'
                        noOptionsText='Không tìm thấy ĐVT'
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        type='number'
                        value={line.received_qty}
                        onChange={(event) => onChangeLine(line.id, { received_qty: event.target.value || '0' })}
                        inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        type='number'
                        value={line.unit_cost}
                        onChange={(event) => onChangeLine(line.id, { unit_cost: event.target.value || '0' })}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={lineAmount}
                        displayType='text'
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={2}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500, fontFamily: '"Roboto Mono", monospace' }}>
                            {value}
                          </Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.lot_no}
                        onChange={(event) => onChangeLine(line.id, { lot_no: event.target.value })}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.note}
                        onChange={(event) => onChangeLine(line.id, { note: event.target.value })}
                        disabled={disabled}
                        fullWidth
                        sx={editableCellSx}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(displayLines.length > 0 || allowAddItem) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pr: 1, gap: 1 }}>
          <Box sx={{ minHeight: 36, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {allowAddItem && (
              <Button
                variant='contained'
                size='small'
                onClick={onAddItem}
                disabled={disabled}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Thêm item
              </Button>
            )}
            {selectedCount > 0 && (
              <>
                <TextField
                  select
                  size='small'
                  value={selectedAction}
                  onChange={(event) => setSelectedAction(event.target.value)}
                  disabled={disabled}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value=''>Actions ({selectedCount})</MenuItem>
                  <MenuItem value='delete'>Delete</MenuItem>
                </TextField>
                <Button
                  variant='outlined'
                  color='inherit'
                  onClick={handleApplyAction}
                  disabled={!selectedAction}
                  size='small'
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Apply
                </Button>
              </>
            )}
          </Box>
          {displayLines.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                borderRadius: 1,
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>Tổng giá trị nhập:</Typography>
              <Typography sx={{ fontSize: '1rem', color: '#1e293b', fontWeight: 700, fontFamily: '"Roboto Mono", monospace' }}>
                <NumericFormat
                  value={totalAmount}
                  displayType='text'
                  thousandSeparator='.'
                  decimalSeparator=','
                  decimalScale={2}
                  fixedDecimalScale={false}
                  renderText={(value) => (`${value} đ`)}
                />
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
